import React, { useMemo, useState } from 'react';
import Toolbar from '@material-ui/core/Toolbar';
import Box from '@material-ui/core/Box';
import AppBar from '@material-ui/core/AppBar/AppBar';
import cx from 'classnames';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { useQuery } from '@apollo/react-hooks';

import MenuIcon from '../../assets/icons/MenuIcon';
import { SearchInput } from '../SearchInput';
import { useEventListener } from '../../utils/hooks';
import IKImage from '../IKImage/IKImage';
import { colorsPalette } from '../../theme';
import useStyles from './styles';
import { Navigation, NavigationItem, NavigationItemType, WPUser } from '../../types';
import { translate } from '../../utils/helpers';
import { CustomButton } from '../UI/CustomButton';
import AccountIcon from '../../assets/icons/AccountIcon';
import { LOGGED_IN_ITEMS, PROFILE_LINKS } from './const';
import { OverlayWrapper } from '../OverlayWrapper';
import { MenuItems } from './components/MenuItems';
import { UserInfoDropdown } from './components/UserInfoDropdown';
import { GET_HAS_NEWS } from '../../graphql/local';
import { SecondaryButton } from '../UI/SecondaryButton';

const NEWS_URL = '/discover/insights';

const LOGO_TRANSFORMATION = [
  {
    height: 120,
    ar: '5.6-1',
    cm: 'pad_resize',
    focus: 'left',
  },
];

interface HeaderProps {
  logoUrl?: string;
  userLoading?: boolean;
  onlyLogo?: boolean;
  navigation?: Navigation;
  userInfo?: WPUser;
  width: Breakpoint;
  assignedLearningsName?: string;
}

export const Header: React.FC<HeaderProps> = ({
  logoUrl,
  navigation,
  userInfo,
  userLoading,
  onlyLogo = false,
  width,
  assignedLearningsName,
}) => {
  const [pageYOffset, setPageYOffset] = useState(0);
  const [isSearchOpen, setSearchOpenState] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const [userInfoOpened, setUserInfoOpened] = useState(false);

  const classes = useStyles(pageYOffset)();
  const isTablet = !isWidthUp('xl', width);
  const isMobile = !isWidthUp('sm', width);
  const userName = userInfo?.username?.split(' ')[0];

  const toggleMenu = () => setMenuOpened((prevState) => !prevState);
  const toggleUserInfo = () => setUserInfoOpened((prevState) => !prevState);

  const { data } = useQuery(GET_HAS_NEWS);
  const hasNews = data?.hasNews;

  const navigationItems: NavigationItem[] = useMemo(() => {
    return navigation?.items
      ? (navigation?.items.filter(
          (item) => item?.item_type === NavigationItemType.Item,
        ) as NavigationItem[])
      : [];
  }, [navigation]);

  const teamName = useMemo(() => {
    if (userInfo?.teams_where_learner?.[0]) {
      return userInfo.teams_where_learner[0]?.name;
    } else if (userInfo?.teams_where_manager?.[0]) {
      return userInfo.teams_where_manager[0]?.name;
    }

    return null;
  }, [userInfo]);

  const toggleSearch = () => {
    setSearchOpenState(!isSearchOpen);
  };

  const handleOpenUserComWidget = () => {
    //@ts-ignore
    if (window?.userengage) {
      //@ts-ignore
      window.userengage('widget.open');
    }
  };

  const handleClickContact = () => {
    handleOpenUserComWidget();
    toggleUserInfo();
  };

  useEventListener(
    'scroll',
    () => {
      if (window) {
        const newPageYOffset = window.pageYOffset;

        if (pageYOffset === 0 && newPageYOffset > 0) {
          setPageYOffset(newPageYOffset);
        } else if (pageYOffset > 0 && newPageYOffset === 0) {
          setPageYOffset(newPageYOffset);
        }
      }
    },
    undefined,
    { passive: true },
  );

  const buttonData = navigationItems?.find((item) => item.type === 'Button');
  const filledButtonData = navigationItems?.find((item) => item.type === 'FilledButton');
  const linksData = navigationItems?.filter((item) => item.type === 'Link');
  const userData = navigationItems?.find((item) => item.type === 'User');

  const chosenItems = useMemo(() => {
    if (userInfo) {
      const arr = LOGGED_IN_ITEMS.map((item) => ({
        ...item,
        title: translate(`header.${item.title}`),
        subItems: item.subItems
          ? item.subItems
              .filter((item) => (item.url === NEWS_URL && hasNews) || item.url !== NEWS_URL)
              .map((subItem) => ({
                ...subItem,
                title: translate(`header.${subItem.title}`),
                description: translate(`header.${subItem.description}`),
              }))
          : [],
      }));

      if (userInfo?.teams_where_manager?.length || userInfo?.teams_where_learner?.length) {
        const assignedLearningTitle = assignedLearningsName
          ? assignedLearningsName
          : translate('header.assigned');
        const url = userInfo?.manager ? '/assigned-learning/dashboard' : '/assigned-learning';

        arr.push({
          url,
          title: `${assignedLearningTitle} ${translate('header.learning')}`,
          subItems: [],
        });
      }

      return arr;
    } else {
      return linksData || [];
    }
  }, [linksData, userInfo, assignedLearningsName, hasNews]);

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      className={cx(classes.header, isSearchOpen && classes.openSearch)}
    >
      <Toolbar className={cx(classes.toolBar, pageYOffset > 0 && 'scrolled-toolbar')}>
        <div
          className={cx(
            classes.logoWrapper,
            isSearchOpen && classes.openSearch,
            pageYOffset > 0 ? 'header_logo_small' : 'header_logo',
          )}
        >
          <a href={userInfo ? '/home' : '/'}>
            {logoUrl && (
              <IKImage
                path={logoUrl || ''}
                draggable={false}
                alt={'logo'}
                transformation={LOGO_TRANSFORMATION}
                // We want to disable srcset for logo for now. It causes an issue when we have custom CSS styles for diff logo and they change the width
                // srcset={[{ width: 141 }, { width: 175 }]}
                // sizes={`(min-width:${BREAKPOINTS_VALUES["sm"]}px) 175px, 141px`}
              />
            )}
          </a>
        </div>
        {userLoading || onlyLogo ? (
          <div></div>
        ) : (
          <>
            {!isSearchOpen && (
              <Box className={classes.topNavMenu}>
                <MenuItems
                  items={chosenItems}
                  isMobile={isTablet}
                  scrolled={pageYOffset > 0}
                  isManager={userInfo?.manager}
                />
              </Box>
            )}
            <Box className={cx(classes.topBar, isSearchOpen && classes.openSearch)}>
              <MenuIcon className={classes.topNavIcon} onClick={toggleMenu} />
              {!userInfo && (
                <>
                  <div>
                    {buttonData && !isTablet && (
                      <a className={classes.joinBtnLink} href={buttonData?.url}>
                        <CustomButton btnClass={classes.joinBtn}>{buttonData?.title}</CustomButton>
                      </a>
                    )}
                    {filledButtonData && !isMobile && (
                      <a className={classes.joinBtnLink} href={filledButtonData?.url}>
                        <SecondaryButton btnClass={classes.joinBtn}>
                          {filledButtonData?.title}
                        </SecondaryButton>
                      </a>
                    )}
                  </div>
                  {userData && (
                    <a
                      href={userData?.url}
                      className={cx(classes.accBtn, isSearchOpen && classes.openSearch)}
                    >
                      <AccountIcon
                        color={colorsPalette.white}
                        aria-label="account of current user"
                      />
                    </a>
                  )}
                </>
              )}
              {userInfo && (
                <>
                  <IconButton onClick={toggleUserInfo} className={classes.topNavButton}>
                    <AccountIcon className={classes.topNavIcon} />
                  </IconButton>
                  <UserInfoDropdown
                    userName={userInfo?.username}
                    teamName={teamName}
                    scrolled={pageYOffset > 0}
                    handleOpenUserComWidget={handleOpenUserComWidget}
                  />
                  <Typography className={classes.username}>{userName}</Typography>
                  <SearchInput
                    wrapperClass={cx(classes.searchWrapper, isSearchOpen && classes.openSearch)}
                    isOpen={isSearchOpen}
                    toggle={toggleSearch}
                  />
                </>
              )}
            </Box>
            <OverlayWrapper
              opened={menuOpened}
              handleClose={toggleMenu}
              logoUrl={logoUrl}
              startBreakpoint="xl"
            >
              <>
                <Box className={cx(classes.overlayContent, userInfo && 'without-last-border')}>
                  <Box className={classes.overlayContainer}>
                    <MenuItems
                      wrapperClass={!userInfo && buttonData ? classes?.menuItemsWithMargin : ''}
                      items={chosenItems}
                      isManager={userInfo?.manager}
                      isMobile={isTablet}
                    />
                    {!userInfo && buttonData && (
                      <a className={classes.joinBtnLink} href={buttonData?.url}>
                        <CustomButton btnClass={classes.joinBtn}>{buttonData?.title}</CustomButton>
                      </a>
                    )}
                    {!userInfo && isMobile && filledButtonData && (
                      <a className={classes.joinBtnLink} href={filledButtonData?.url}>
                        <SecondaryButton btnClass={classes.joinBtn}>
                          {filledButtonData?.title}
                        </SecondaryButton>
                      </a>
                    )}
                  </Box>

                  {userInfo ? (
                    <a href="/logout" className={classes.overlayBottomLink}>
                      {translate('header.logout')}
                    </a>
                  ) : (
                    <a href="/login" className={classes.overlayBottomLink}>
                      {translate('header.login')}
                    </a>
                  )}
                </Box>
              </>
            </OverlayWrapper>
            {userInfo && (
              <OverlayWrapper
                opened={userInfoOpened}
                handleClose={toggleUserInfo}
                logoUrl={logoUrl}
              >
                <Box className={cx(classes.overlayContent, userInfo && 'without-last-border')}>
                  {userInfo && (
                    <>
                      <div className={classes.overlayMainContent}>
                        <Box className={classes.userInfoWrapper}>
                          <Typography className={classes.username}>{userName}</Typography>
                          {teamName && <Typography className={classes.team}>{teamName}</Typography>}
                        </Box>
                        <Box className={cx(classes.accordionLinks)}>
                          {PROFILE_LINKS.map((item) => (
                            <Box key={item.url} className={classes.linkWrapper}>
                              <a href={item.url} className={classes.link}>
                                {translate(item.title)}
                              </a>
                            </Box>
                          ))}
                          {
                            // @ts-ignore
                            !!window?.userengage && (
                              <Box className={classes.linkWrapper}>
                                <div className={classes.link} onClick={handleClickContact}>
                                  {translate('header.contact')}
                                </div>
                              </Box>
                            )
                          }
                        </Box>
                      </div>
                    </>
                  )}
                  <a href="/logout" className={classes.overlayBottomLink}>
                    {translate('header.logout')}
                  </a>
                </Box>
              </OverlayWrapper>
            )}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default withWidth()(Header);
